import { useState } from "react";
import { Card } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";

import Dropdown from "components/atoms/Dropdown/Dropdown";
import Button from "components/atoms/Button/Button";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import { downloadFileImportTemplate } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import DownloadTemplateLocationFilter from "./DownloadTemplateLocationFilter";
import DownloadTemplateRoleFilter from "./DownloadTemplateRoleFilter";
import DownloadTemplateRateCardFilter from "./DownloadTemplateRateCardFilter";

const getDownloadFileName = (contentDisposition: string | undefined, fallback: string) => {
  if (!contentDisposition) return fallback;

  const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
  if (!fileNameMatch?.[1]) return fallback;

  return fileNameMatch[1]
    .replace(/['"]/g, "")
    .trim()
    .replace(/\.xlsx_+$/i, ".xlsx");
};

export default function DownloadTemplate() {
  const methods = useForm();
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { admin_settings, quoting, off_platform } = JSON.parse(permissions || "{}")?.permissions || {};
  const { company_shipper_settings = {} } = JSON.parse(permissions || "{}") || {};
  const [isLoading, setIsLoading] = useState(false);
  const selectedTemplateType = methods.watch("template_type");

  const validateParams = () => {
    if (
      methods.getValues("template_type") === "user" &&
      methods.getValues("action_type") === "bulk_update" &&
      (!methods.getValues("selected_payment_locations")?.length || !methods.getValues("selected_user_roles")?.length)
    ) {
      if (!methods.getValues("selected_payment_locations")?.length) {
        methods.setError("selected_payment_locations", {
          type: "required",
          message: intl.formatMessage({ id: "general.requiredField", defaultMessage: "This is a required field" }),
        });
      }
      if (!methods.getValues("selected_user_roles")?.length) {
        methods.setError("selected_user_roles", {
          type: "required",
          message: intl.formatMessage({ id: "general.requiredField", defaultMessage: "This is a required field" }),
        });
      }
      return false;
    }
    return true;
  };

  const handleDownloadLocationReference = () => {
    const link = document.createElement("a");
    const staticContentURL = import.meta.env.REACT_APP_STATIC_CONTENT_URL;
    link.href = `${staticContentURL}/files/LocationReference.xlsx`;
    link.download = "LocationReference.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onSubmit = (data: any) => {
    if (!validateParams()) {
      return;
    }
    const { selected_payment_locations, selected_user_roles, ...rest } = data;
    const params = {
      ...rest,
      ...(selected_payment_locations?.length && { selected_payment_locations: selected_payment_locations.join(",") }),
      ...(selected_user_roles?.length && { selected_user_roles: selected_user_roles.join(",") }),
    };
    setIsLoading(true);
    downloadFileImportTemplate({ params })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        const fallbackFileName = `${params.template_type}_${params.action_type}.xlsx`;
        link.setAttribute("download", getDownloadFileName(res.headers["content-disposition"], fallbackFileName));
        link.click();
      })
      .catch((err) => {
        errorToast(err, "Could not export file. Please try again later.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTemplateTypeChange = (val: any) => {
    if (
      (val?.value === "off_platform" || val?.value === "rate_card") &&
      methods.getValues("action_type") === "bulk_update"
    ) {
      methods.setValue("action_type", "bulk_create");
    }
  };

  const isBulkCreateOnlyTemplate = selectedTemplateType === "off_platform" || selectedTemplateType === "rate_card";

  return (
    <FormProvider {...methods}>
      <Card body className="shadow-none quote-container">
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <h4>
            <FormattedMessage id="settings.fileImportTools.downloadTemplate" defaultMessage="Download Template" />
          </h4>
          <p className="mt-2 mb-3 text-primary font-b3">
            <FormattedMessage
              id="settings.fileImportTools.downloadTemplateDesc"
              defaultMessage="Prepare template file to be downloaded and then filled. The file can only be opened using MS. Excel 2017 and above. Do not add new columns to the template"
            />
          </p>
          <div className="d-flex align-items-center flex-wrap">
            <div className="w-192 dropdown-div mr-3">
              <p className="mb-2 mt-1 ml-1 font-b4 required">
                <FormattedMessage id="settings.fileImportTools.selectData" defaultMessage="Select Data" />
              </p>
              <Dropdown
                useHookForm
                placeholder={intl.formatMessage({
                  id: "settings.fileImportTools.dropdownPlaceholder",
                  defaultMessage: "Choose an option...",
                })}
                name="template_type"
                onChange={handleTemplateTypeChange}
                options={[
                  ...(admin_settings?.perform_file_import && admin_settings?.view_users
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.user",
                            defaultMessage: "User",
                          }),
                          value: "user",
                        },
                      ]
                    : []),
                  ...(admin_settings?.perform_file_import && !quoting?.restricted_to_address_book
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.address",
                            defaultMessage: "Address",
                          }),
                          value: "address",
                        },
                      ]
                    : []),
                  ...(admin_settings?.perform_file_import
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.contact",
                            defaultMessage: "Contact",
                          }),
                          value: "contact",
                        },
                      ]
                    : []),
                  ...(off_platform?.create_tracker && company_shipper_settings?.allow_off_platform_shipments
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.tracker",
                            defaultMessage: "Tracker",
                          }),
                          value: "off_platform",
                        },
                      ]
                    : []),
                  ...(admin_settings?.perform_rate_card_file_import
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "settings.fileImportTools.rateCards",
                            defaultMessage: "Rate Cards",
                          }),
                          value: "rate_card",
                        },
                      ]
                    : []),
                ]}
                required
              />
            </div>
            <div className="w-192 dropdown-div">
              <p className="mb-2 mt-1 ml-1 font-b4 required">
                <FormattedMessage id="settings.fileImportTools.selectAction" defaultMessage="Select Action (Create/Edit)" />
              </p>
              <Dropdown
                useHookForm
                placeholder={intl.formatMessage({
                  id: "settings.fileImportTools.dropdownPlaceholder",
                  defaultMessage: "Choose an option...",
                })}
                name="action_type"
                options={[
                  {
                    label: intl.formatMessage({
                      id: "settings.fileImportTools.createFileImport.bulkCreate",
                      defaultMessage: "Bulk create",
                    }),
                    value: "bulk_create",
                  },
                  ...(isBulkCreateOnlyTemplate
                    ? []
                    : [
                        {
                          label: intl.formatMessage({
                            id: "settings.fileImportTools.createFileImport.bulkEdit",
                            defaultMessage: "Bulk edit",
                          }),
                          value: "bulk_update",
                        },
                      ]),
                ]}
                required
              />
            </div>
          </div>
          {selectedTemplateType === "rate_card" && (
            <div className="mt-3">
              <DownloadTemplateRateCardFilter />
            </div>
          )}
          {methods.watch("template_type") === "user" && methods.watch("action_type") === "bulk_update" && (
            <>
              <div className="mt-3">
                <DownloadTemplateLocationFilter />
              </div>
              <div className="mt-3">
                <DownloadTemplateRoleFilter />
              </div>
              <div className="mt-3">
                <div className="d-flex flex-wrap">
                  <div className="mb-xs-3 mb-sm-3 download-filter-title">
                    <div className="mb-1">
                      <h6>
                        <FormattedMessage id="settings.fileImportTools.lastActive" defaultMessage="Last Active" />
                      </h6>
                    </div>
                    <div className="font-b3 text-secondary">
                      <FormattedMessage
                        id="settings.fileImportTools.chooseLastActiveDate"
                        defaultMessage="Choose last active date"
                      />
                    </div>
                  </div>
                  <div className="w-192">
                    <p className="mb-2 mt-1 ml-1 font-b3 required">Before</p>
                    <DatePicker
                      name="last_active_before"
                      shouldUnregister
                      required
                      maxDate={moment().toDate()}
                      defaultValue={moment().format("YYYY-MM-DD")}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="mt-3 d-flex align-items-center">
            <Button variant="outline-primary" type="submit" isLoading={isLoading} className="mr-4">
              <FormattedMessage id="settings.fileImportTools.downloadTemplate" defaultMessage="Download Template" />
            </Button>
            {company_shipper_settings?.enable_new_address_structure && methods.watch("template_type") === "address" && (
              <Button variant="outline-primary" className="ml-5" onClick={handleDownloadLocationReference}>
                <FormattedMessage
                  id="settings.fileImportTools.downloadLocationReference"
                  defaultMessage="Download Location Reference"
                />
              </Button>
            )}
          </div>
        </form>
      </Card>
    </FormProvider>
  );
}
